import { useCompare } from "@/hooks/ixd-dsd-compare/use-compare";
import { cn } from "@/lib/utils";
import { Button } from "@/toggle-ui-kit/button";
import { ViewSwitchGroup } from "@/toggle-ui-kit/view-switch-group";
import { EXISTING_REPORT_ENTRY_URL } from "@/utils/ixd-dsd-compare/comparison-url";
import { EXISTING_IXD_ENTRY_URL } from "@/utils/ixd-preview-compare/compare-url";
import { startIxdIxdCompare } from "@/utils/ixd-preview-compare/result-tab";
import { logHomeServiceAccess, logHomeWork } from "@/utils/usage-log";
import { GitCompare, Loader2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { FileDropArea } from "@/components/file-drop-zone";
import { acceptErrorMessage } from "@/utils/file-accept";
import {
  duplicateError,
  fillFileSlots,
  overflowError,
  splitDsdIxd,
  type FileSlots,
} from "./file-slots";
import { FileSlotPair } from "./file-slot-pair";
import { IxdFileGuide, Notice } from "./notice";

type Tab = "dsd-ixd" | "ixd-ixd";

const TABS: { value: Tab; label: string }[] = [
  { value: "dsd-ixd", label: "DSD ↔ IXD 대사" },
  { value: "ixd-ixd", label: "IXD ↔ IXD 대사" },
];

const REPLACE_HINT = "다른 파일로 교체하려면 클릭하거나 드래그하세요";

const DSD_IXD_ACCEPT = ".dsd,.ixd";
const IXD_ACCEPT = ".ixd";

// 집계에서 STEP 카드 로그와 묶이도록 steps.tsx 의 label 과 같게 쓴다
const STEP_LABEL = "보고서 최종 검토";

// 두 탭이 같은 버튼을 가져서, 로그에는 탭 이름을 붙여 어느 대사였는지 남긴다
const logLoadPrevious = (tab: Tab) =>
  logHomeServiceAccess(
    `${STEP_LABEL} 패널`,
    `${tabLabel(tab)} · 이전 IXD 불러오기`,
  );

const logStart = (tab: Tab) => logHomeWork(STEP_LABEL, tabLabel(tab));

const tabLabel = (tab: Tab) => TABS.find((t) => t.value === tab)?.label ?? tab;

const Actions = ({
  pending = false,
  disabled,
  onLoadPrevious,
  onStart,
}: {
  pending?: boolean;
  disabled: boolean;
  onLoadPrevious: () => void;
  onStart: () => void;
}) => (
  <div className="mt-2 flex w-full items-start justify-between">
    <Button size={40} onClick={onLoadPrevious}>
      이전 IXD 불러오기
    </Button>
    <Button
      variant="solid"
      size={40}
      className="bg-blue-500"
      leftIcon={
        pending ? (
          <Loader2 className="animate-spin" strokeWidth={1.8} />
        ) : (
          <GitCompare strokeWidth={1.8} />
        )
      }
      disabled={disabled || pending}
      onClick={onStart}
    >
      대사 시작
    </Button>
  </div>
);

/** 대사 화면(/ixd-dsd-compare)과 같은 훅으로 올려 결과를 새 탭으로 연다. */
const DsdIxdTab = () => {
  const [dsdFile, setDsdFile] = useState<File | null>(null);
  const [ixdFile, setIxdFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { isComparing, handleCompare, canCompare } = useCompare({
    dsdFile,
    ixdFile,
    selectedIxdReportId: null,
    sourceMode: "upload",
  });

  return (
    <>
      <Notice>대사를 위해 DSD/IXD파일을 1개씩 업로드해주세요</Notice>
      {/* 칸은 확장자로 정한다 — 같은 형식을 또 고르면 그 칸을 교체한다 */}
      <FileDropArea
        accept={DSD_IXD_ACCEPT}
        hint="*.dsd, .ixd 형식"
        maxFiles={2}
        selectedHint={dsdFile && ixdFile ? REPLACE_HINT : undefined}
        error={error}
        onReject={({ message }) => setError(message)}
        onFilesSelect={(files, { rejected }) => {
          const { dsd, ixd, error: formatError } = splitDsdIxd(files);
          const invalid =
            formatError ?? duplicateError([dsdFile, ixdFile], files);
          // 형식 거부는 받은 파일의 반영을 막지 않으므로, 드롭을 실제로 막은
          // 검증 오류가 있으면 그 문구를 먼저 보여준다
          setError(
            invalid ??
              (rejected > 0 ? acceptErrorMessage(DSD_IXD_ACCEPT) : null),
          );
          // 검증에 걸리면 칸은 그대로 둔다 — 고른 파일 중 하나만 조용히 올리지 않는다
          if (invalid) return;
          if (dsd) setDsdFile(dsd);
          if (ixd) setIxdFile(ixd);
        }}
      >
        {(dsdFile || ixdFile) && (
          <FileSlotPair
            slots={[
              {
                label: "DSD 파일",
                format: "dsd",
                file: dsdFile,
                onRemove: () => {
                  setDsdFile(null);
                  setError(null);
                },
              },
              {
                label: "IXD 파일",
                format: "xbrl",
                file: ixdFile,
                onRemove: () => {
                  setIxdFile(null);
                  setError(null);
                },
              },
            ]}
          />
        )}
      </FileDropArea>
      <Actions
        pending={isComparing}
        disabled={!canCompare}
        onLoadPrevious={() => {
          logLoadPrevious("dsd-ixd");
          navigate(EXISTING_REPORT_ENTRY_URL);
        }}
        onStart={() => {
          logStart("dsd-ixd");
          handleCompare();
        }}
      />
    </>
  );
};

/** 대사 화면(/ixd-preview-compare)과 같은 결과 탭을 연다. 업로드 파일은 저장되지 않는다. */
const IxdIxdTab = () => {
  const [[prev, latest], setFiles] = useState<FileSlots>([null, null]);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  return (
    <>
      <Notice>대사를 위해 IXD파일 2개를 업로드해주세요</Notice>
      <FileDropArea
        accept={IXD_ACCEPT}
        hint="*.ixd 형식"
        maxFiles={2}
        selectedHint={prev && latest ? REPLACE_HINT : undefined}
        error={error}
        onReject={({ message }) => setError(message)}
        onFilesSelect={(files, { rejected }) => {
          // 같은 파일끼리 대사하면 결과가 없고, 남은 칸보다 많이 고르면 뒤엣것이
          // 앞엣것을 덮는다 — 둘 다 칸을 바꾸지 않고 인라인으로 알린다
          const invalid =
            overflowError([prev, latest], files) ??
            duplicateError([prev, latest], files);
          setError(
            invalid ?? (rejected > 0 ? acceptErrorMessage(IXD_ACCEPT) : null),
          );
          if (invalid) return;
          setFiles((slots) => fillFileSlots(slots, files));
        }}
      >
        {(prev || latest) && (
          <FileSlotPair
            slots={[
              {
                label: "이전 파일",
                format: "xbrl",
                file: prev,
                onRemove: () => {
                  setFiles(([, l]) => [null, l]);
                  setError(null);
                },
              },
              {
                label: "최신 파일",
                format: "xbrl",
                file: latest,
                onRemove: () => {
                  setFiles(([p]) => [p, null]);
                  setError(null);
                },
              },
            ]}
            onSwap={() => setFiles(([p, l]) => [l, p])}
          />
        )}
      </FileDropArea>
      <Actions
        disabled={!prev || !latest}
        onLoadPrevious={() => {
          logLoadPrevious("ixd-ixd");
          navigate(EXISTING_IXD_ENTRY_URL);
        }}
        onStart={() => {
          if (!prev || !latest) return;
          logStart("ixd-ixd");
          // 결과 탭이 opener 에게 ready 를 알려야 파일을 받으므로 pending 탭 우회가 없다
          startIxdIxdCompare(
            { type: "ixd_file", ixdFile: prev },
            { type: "ixd_file", ixdFile: latest },
            [{ filename: prev.name }, { filename: latest.name }],
          );
        }}
      />
    </>
  );
};

/** STEP6 보고서 최종 검토 — DSD↔IXD, IXD↔IXD 대사를 탭으로 둔다. */
export const FinalReviewPanel = () => {
  const [tab, setTab] = useState<Tab>("dsd-ixd");

  return (
    <>
      <div className="flex w-full flex-col items-end gap-4">
        <ViewSwitchGroup
          size="36px"
          options={TABS}
          value={tab}
          onChange={setTab}
          className="flex w-full [&>button]:flex-1"
        />
        {/* 탭을 오가도 올린 파일이 남도록 둘 다 마운트해 두고 숨긴다 */}
        {TABS.map(({ value }) => (
          <div
            key={value}
            hidden={tab !== value}
            className={cn(
              "flex w-full flex-col items-end gap-4",
              tab !== value && "hidden",
            )}
          >
            {value === "dsd-ixd" ? <DsdIxdTab /> : <IxdIxdTab />}
          </div>
        ))}
      </div>
      <IxdFileGuide />
    </>
  );
};
